import React, { Component } from "react";
import { observer } from "mobx-react";

import { OrganizationFeatureFlag } from "@formant/core/model/OrganizationFeatureFlag";
import { ResourcePolicy } from "@formant/core/model/Policies";
import { Resource } from "@formant/core/model/Resource";
import { IconName } from "@formant/ui/component/common/Icon";

import { track } from "../../analytics";
import { organizationStore } from "../../stores";
import { hashObservable } from "../../utils/hashObservable";
import {
    IRestrictedListParameters,
    RestrictedList,
} from "../common/RestrictedList";
import { SideTabs } from "../common/Tabs";
import { ITab } from "../common/Tabs/ITab";
import { Adapters } from "./Adapters";
import { AnnotationTemplates } from "./AnnotationTemplates";
import { CloudFiles } from "./CloudFiles";
import { CommandTemplates } from "./CommandTemplates";
import { CommandTemplates as CommandTemplatesV2 } from "./CommandTemplatesV2/CommandTemplates";
import { DeviceConfigurationTemplates } from "./DeviceConfigurationTemplates";
import { Devices } from "./Devices";
import { EventTriggers } from "./EventTriggers";
import { Groups } from "./Groups";
import { Integrations } from "./Integrations";
import { Modules } from "./Modules";
import { Organization } from "./Organization";
import { Overview } from "./Overview";
import { Publish } from "./Publish";
import { Roles } from "./Roles";
import { Schedule } from "./Schedules";
import { Shares } from "./Shares";
import { Streams } from "./Streams";
import { TagTemplates } from "./TagTemplates";
import { Teams } from "./Teams";
import { Users } from "./Users";
import { Views } from "./Views";

export const getRestrictedTabs = (): IRestrictedListParameters<ITab>[] => {
    return [
        {
            items: [
                {
                    name: "Users",
                    icon: "user",
                    content: <Users />,
                    group: 0,
                },
            ],
            resource: "users",
            requiredAccess: "administrator",
            paidOnly: true,
            featureFlag: "settings.user",
        },
        {
            items: [
                {
                    name: "Teams",
                    icon: "teams",
                    content: <Teams />,
                    group: 0,
                },
            ],
            resource: "users",
            requiredAccess: "administrator",
            requiredFeature: "teams",
            paidOnly: true,
            featureFlag: "settings.team",
        },
        {
            items: [
                {
                    name: "Roles",
                    icon: "access",
                    content: <Roles />,
                    group: 0,
                },
            ],
            resource: "users",
            requiredAccess: "administrator",
            requiredFeature: "roleViewer",
            paidOnly: true,
            featureFlag: "settings.role",
            disabled: false,
        },
        {
            items: [
                {
                    name: "Devices",
                    icon: "device",
                    content: <Devices />,
                    group: 1,
                },
            ],
            resource: "devices",
            requiredAccess: "administrator",
            featureFlag: "settings.device",
        },
        {
            items: [
                {
                    name: "Groups",
                    icon: "group",
                    content: <Groups />,
                    group: 1,
                },
            ],
            resource: "devices",
            requiredAccess: "administrator",
            requiredFeature: "advancedConfiguration",
            paidOnly: true,
            featureFlag: "settings.group",
        },
        {
            items: [
                {
                    name: "Config templates",
                    icon: "configuration",
                    content: <DeviceConfigurationTemplates />,
                    group: 1,
                },
            ],
            resource: "devices",
            requiredAccess: "administrator",
            paidOnly: true,
            featureFlag: "settings.config_template",
            disabled: !organizationStore.deviceConfigurationTemplatesEnabled(),
        },
        {
            items: [
                {
                    name: "Events",
                    icon: "event",
                    content: <EventTriggers />,
                    group: 2,
                },
            ],
            resource: "events",
            requiredFeature: "events",
            requiredAccess: "administrator",
            paidOnly: true,
            featureFlag: "settings.event",
        },
        {
            items: [
                {
                    name: "Views",
                    icon: "view",
                    content: <Views />,
                    group: 2,
                },
            ],
            resource: "views",
            requiredAccess: "edit-tags",
            requiredFeature: "observability",
            paidOnly: true,
            featureFlag: "settings.view",
        },
        {
            items: [
                {
                    name: "Commands",
                    icon: "command",
                    content: <CommandTemplates />,
                    group: 2,
                },
            ],
            resource: "commands",
            requiredAccess: "administrator",
            requiredFeature: "commands",
            paidOnly: true,
            featureFlag: "settings.command",
            // disabled: organizationStore.getCommandsModuleEnabled(),
        },
        {
            items: [
                {
                    name: "Commands v2",
                    icon: "command",
                    content: <CommandTemplatesV2 />,
                    group: 2,
                },
            ],
            resource: "commands",
            requiredAccess: "administrator",
            requiredFeature: "commands",
            paidOnly: true,
            featureFlag: "settings.command",
            disabled: false,
        },
        {
            items: [
                {
                    name: "Schedules",
                    icon: "calendar",
                    content: <Schedule />,
                    group: 2,
                },
            ],
            resource: "schedules",
            requiredAccess: "administrator",
            paidOnly: true,
            featureFlag: "settings.schedule",
        },
        {
            items: [
                {
                    name: "Annotations",
                    icon: "annotation" as IconName,
                    content: <AnnotationTemplates />,
                    group: 2,
                },
            ],
            resource: "annotations" as Resource,
            requiredAccess: "administrator" as ResourcePolicy<Resource>,
            paidOnly: true,
            featureFlag: "settings.annotation" as OrganizationFeatureFlag,
        },
        {
            items: [
                {
                    name: "Shares",
                    icon: "share",
                    content: <Shares />,
                    group: 2,
                },
            ],
            resource: "share",
            requiredAccess: "administrator",
            requiredFeature: "share",
            paidOnly: true,
            featureFlag: "settings.share",
        },
        {
            items: [
                {
                    name: "Publish",
                    icon: "share",
                    content: <Publish />,
                    group: 2,
                },
            ],
            resource: "share",
            requiredAccess: "administrator",
            requiredFeature: "share",
            paidOnly: true,
            featureFlag: "settings.share",
        },
        {
            items: [
                {
                    name: "Streams",
                    icon: "stream",
                    content: <Streams />,
                    group: 3,
                },
            ],
            resource: "streams",
            requiredAccess: "administrator",
            requiredFeature: "observability",
            paidOnly: true,
            featureFlag: "settings.stream",
        },
        {
            items: [
                {
                    name: "Tags",
                    icon: "tags",
                    content: <TagTemplates />,
                    group: 3,
                },
            ],
            resource: "devices",
            requiredAccess: "administrator",
            paidOnly: true,
            featureFlag: "settings.tag",
        },
        {
            items: [
                {
                    name: "Integrations",
                    icon: "integration",
                    content: <Integrations />,
                    group: 4,
                },
            ],
            resource: "integrations",
            requiredAccess: "administrator",
            requiredFeature: "advancedConfiguration",
            paidOnly: true,
            featureFlag: "settings.integration",
        },
        {
            items: [
                {
                    name: "Cloud files",
                    icon: "file",
                    content: <CloudFiles />,
                    group: 4,
                },
            ],
            resource: "fileStorage",
            requiredAccess: "administrator",
            paidOnly: true,
            featureFlag: "settings.file_storage",
        },
        {
            items: [
                {
                    name: "Adapters",
                    icon: "adapter",
                    content: <Adapters />,
                    group: 4,
                },
            ],
            resource: "devices",
            requiredAccess: "administrator",
            requiredFeature: "adapters",
            paidOnly: true,
            featureFlag: "settings.adapter",
            disabled: false,
        },
        {
            items: [
                {
                    name: "Modules",
                    icon: "utility",
                    content: <Modules />,
                    group: 4,
                },
            ],
            resource: "devices",
            requiredAccess: "administrator",
            paidOnly: true,
            featureFlag: "settings.module",
            disabled: false,
        },
        {
            items: [
                {
                    name: "Organization",
                    icon: "organization",
                    content: <Organization />,
                    group: 5,
                },
            ],
            resource: "organization",
            requiredAccess: "administrator",
            paidOnly: true,
            featureFlag: "settings.organization",
        },
    ];
};

@observer
export class Settings extends Component {
    @hashObservable()
    private tab: string | undefined;

    private restrictedTabs = getRestrictedTabs();

    constructor(props: any) {
        super(props);
        this.restrictedTabs = getRestrictedTabs();

        const enableOverview = organizationStore.overviewSettingsEnabled;
        const hasOverviewTab = this.restrictedTabs.some(
            _ => _.items[0].name === "Overview",
        );
        if (enableOverview && !hasOverviewTab) {
            this.restrictedTabs.push({
                items: [
                    {
                        name: "Overview",
                        icon: "overview",
                        content: <Overview />,
                        group: 2,
                    },
                ],
                resource: "views",
                requiredAccess: "administrator",
                paidOnly: true,
                featureFlag: "settings.user",
            });
        }
    }

    public render() {
        const restrictedList = new RestrictedList<ITab>();
        const tabs = this.restrictedTabs.flatMap(_ => restrictedList.new(_));

        return (
            <SideTabs
                title="Settings"
                tabs={tabs}
                selected={this.tab || tabs[0]?.name || ""}
                onSelected={this.onTabSelected}
            />
        );
    }

    private onTabSelected = (tab: string) => {
        this.tab = tab;
        track("Settings Tab", { Tab: tab });
    };
}
